import { GrandstandButton } from '@/components/Buttons'
import { HeadingLabel, MetaDataLabel } from '@/styles/fonts'
import { GrandstandIcon } from '@grandstand-web/bally-web-shared/src/components/GrandstandIcon'
import { ActiveSubscriptionsResponse, PaymentMethodsResponse } from '@grandstand/presentation-models'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import styled from 'styled-components'
import PaymentMethodUpdateForm from '../payments/checkout/PaymentMethodUpdateForm'

export type CreditCardDetail = PaymentMethodsResponse[number] | null
export type Subscription = ActiveSubscriptionsResponse[number]

type BillingProps = {
  subscriptions: ActiveSubscriptionsResponse
  creditCardDetail: CreditCardDetail
}

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled(HeadingLabel)`
  border-bottom: 1px solid var(--divider-canvas);
  padding-bottom: 25px;
  padding-left: 25px;
  padding-top: 25px;
  cursor: pointer;
  div {
    float: left;
    top: 3px;
    right: 10px;
    position: relative;
  }
`

const FieldDiv = styled.div`
  cursor: pointer;
  border-bottom: 1px solid var(--divider-canvas);

  padding: 60px 25px;
  height: 70px;
  display: flex;
  align-items: center;
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: auto;
`

const FieldTitle = styled(MetaDataLabel)`
  top: 25px;
  margin-bottom: 25px;
`

const FieldLabel = styled(MetaDataLabel)``

const FieldInfo = styled(MetaDataLabel)`
  color: var(--on-background-alt);
`

const FieldEdit = styled(GrandstandButton)`
  width: 30%;
  background-color: var(--surface);
  border: 1px solid var(--surface-light);
  color: var(--surface-light);
  :hover {
    background-color: var(--surface);
    border: 1px solid var(--surface-light);
    color: var(--surface-light);
  }
`

const ArrowIcon = styled(GrandstandIcon)``

const ModalWrapper = styled.div<{ isActive: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  display: ${({ isActive }) => (isActive ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  overflow: hidden;
`
const ModalClickOutside = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
`
const EditPaymentMethodModal = styled.div<{ isActive: boolean }>`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 560px;
  overflow: auto;
  padding: 16px;
  @media (min-width: 440px) {
    padding: 20px;
  }
  @media (min-width: 600px) {
    padding: 0;
  }
`

type BillingDetailProps = {
  subscription: Subscription
  creditCardDetail: CreditCardDetail
  activeSubscription: Subscription | null
  setActiveSubscription: Dispatch<SetStateAction<Subscription | null>>
}
const BillingDetail = ({
  activeSubscription,
  subscription,
  creditCardDetail,
  setActiveSubscription,
}: BillingDetailProps) => {
  const router = useRouter()
  const isActive = activeSubscription && activeSubscription.order_id === subscription.order_id ? true : false
  useEffect(() => {
    if (!isActive) {
      return
    }
    // cancel on escape keyup
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveSubscription(null)
      }
    }
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [isActive, setActiveSubscription])

  return (
    <FieldDiv>
      <FieldWrapper>
        <FieldTitle fontSize="lg">{subscription.service_name}</FieldTitle>
        <FieldLabel fontSize="sm">{subscription.payment_method}</FieldLabel>
        {subscription.payment_method === 'Credit/Debit Card' && (
          <FieldInfo fontSize="sm">{creditCardDetail?.card_number}</FieldInfo>
        )}
      </FieldWrapper>
      {subscription.payment_method === 'Credit/Debit Card' && creditCardDetail ? (
        <FieldEdit
          data-theme="dark"
          buttonStyle="primary"
          buttonSize="sm"
          disabled={activeSubscription ? true : false}
          onClick={() => setActiveSubscription(subscription)}
        >
          Edit
        </FieldEdit>
      ) : (
        <FieldInfo fontSize="sm">{`Billed and managed through ${subscription.payment_method}`}</FieldInfo>
      )}
      {creditCardDetail?.payment_method_id && (
        <ModalWrapper isActive={isActive}>
          <ModalClickOutside
            onClick={() => {
              setActiveSubscription(null)
            }}
          />
          <EditPaymentMethodModal isActive={isActive}>
            <PaymentMethodUpdateForm
              isActive={isActive}
              paymentMethodId={creditCardDetail?.payment_method_id}
              onCancel={() => {
                setActiveSubscription(null)
              }}
              onSuccess={() => {
                setActiveSubscription(null)
                router.reload()
              }}
            />
          </EditPaymentMethodModal>
        </ModalWrapper>
      )}
    </FieldDiv>
  )
}

const Billing = (props: BillingProps) => {
  const router = useRouter()
  const { subscriptions, creditCardDetail } = props
  const [activeSubscription, setActiveSubscription] = useState<Subscription | null>(null)

  const handleBackClick = () => {
    router.back()
  }

  return (
    <MainContent>
      <Header onClick={handleBackClick} fontSize="4">
        <ArrowIcon icon="arrow_left" /> Billing
      </Header>
      {subscriptions.map((subscription: Subscription, index: number) => {
        return (
          <BillingDetail
            key={`BillingDetail${subscription.order_id}`}
            creditCardDetail={creditCardDetail}
            subscription={subscription}
            activeSubscription={activeSubscription}
            setActiveSubscription={setActiveSubscription}
          />
        )
      })}
    </MainContent>
  )
}
export default Billing
